import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, X, Loader2, SearchX, AlertTriangle, Mic, ArrowRight, DollarSign, CalendarPlus, ArrowLeft } from "lucide-react";
import { api, describeError } from "@/lib/api";
import { fmtDob, fmtSex, titleCaseName, parseDob } from "@/lib/patientFormat";

const DROPDOWN_MAX = 6;
const PAGE = 12;

// Recent charts in the empty-search state — wired ahead of its endpoint.
// Flip RECENTS_LIVE to true (and swap the fixture for the fetched response) when
// the recent-charts endpoint lands. DQ: confirm "recent" is scoped to the signed-in USER.
const RECENTS_LIVE = true;
const RECENTS_MAX = 15;
const _rnow = Date.now();
const _rmin = (m) => new Date(_rnow - m * 60000).toISOString();
const RECENTS_FIXTURE = [
  { id: "rc1", name: "Marisol Vega", age: 54, sex: "F", mrn: "440821", last_opened_at: _rmin(6) },
  { id: "rc2", name: "Darnell Pierce", age: 61, sex: "M", mrn: "391002", last_opened_at: _rmin(28) },
  { id: "rc3", name: "Priya Raman", age: 47, sex: "F", mrn: null, last_opened_at: _rmin(72) },
  { id: "rc4", name: "Willa Nakamura", age: 39, sex: "F", mrn: "402210", last_opened_at: _rmin(140) },
  { id: "rc5", name: "Theodore Obi", age: 66, sex: "M", mrn: "377845", last_opened_at: _rmin(300) },
  { id: "rc6", name: "Consuela Ramos", age: 72, sex: "F", mrn: "418033", last_opened_at: _rmin(26 * 60) },
  { id: "rc7", name: "Gideon Frost", age: 58, sex: "M", mrn: null, last_opened_at: _rmin(30 * 60) },
  { id: "rc8", name: "Amara Bello", age: 44, sex: "F", mrn: "455190", last_opened_at: _rmin(52 * 60) },
  { id: "rc9", name: "Hollis Wexler", age: 69, sex: "M", mrn: "360027", last_opened_at: _rmin(74 * 60) },
  { id: "rc10", name: "Yuki Tanaka", age: 51, sex: "F", mrn: "429981", last_opened_at: _rmin(100 * 60) },
  { id: "rc11", name: "Rosalind Achebe", age: 63, sex: "F", mrn: "381145", last_opened_at: _rmin(150 * 60) },
  { id: "rc12", name: "Bennett Cho", age: 35, sex: "M", mrn: "447702", last_opened_at: _rmin(220 * 60) },
];
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(typeof v === "string" && v.length === 10 ? v + "T00:00:00" : v);
  if (isNaN(d)) return String(v);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};
const initialsOf = (name) =>
  (String(name).split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("") || "PT").toUpperCase();
const ageOf = (p) => {
  const d = parseDob(p.dob);
  if (d) { const t = new Date(); let y = t.getFullYear() - d.getFullYear(); if (t.getMonth() < d.getMonth() || (t.getMonth() === d.getMonth() && t.getDate() < d.getDate())) y--; return y; }
  return p.age != null && p.age !== "" ? Number(p.age) : null;
};
const nameOf = (p) => titleCaseName(p.full_name || `${p.first_name || ""} ${p.last_name || ""}`.trim()) || "Patient";
const payerOf = (p) => p.insurance?.payer_name || p.insurance?.carrier_name || p.payer || null;
const uuidOf = (p) => p.chart_uuid || p.chart_id;

function hasMedicalData(p) {
  if (p.has_medical_data === false) return false;
  if (p.has_medical_data === true || p.hasMedicalData === true || p.has_chart_data === true || p.chart_available === true || p.ehi_available === true || p.medical_data) return true;
  const truthy = (v) => (Array.isArray(v) ? v.length > 0 : v && typeof v === "object" ? Object.keys(v).length > 0 : !!v);
  return ["last_office_visit", "last_visit", "next_appointment", "diagnoses", "icd_codes", "cpt_codes", "vitals", "labs", "medications", "allergies", "program_types", "data_sources", "care_provider"].some((k) => truthy(p[k]));
}

// wrap the matched query substring in <mark> so the reason a row surfaced is visible.
// Always returns ONE stable <span> (never a bare string vs. Fragment swap, and never
// empty text nodes) — that mix is what desyncs React's DOM refs and throws
// insertBefore/removeChild "not a child" when these rows unmount.
function highlight(text, q) {
  const s = String(text || "");
  const needle = String(q || "").trim();
  const i = needle ? s.toLowerCase().indexOf(needle.toLowerCase()) : -1;
  if (i < 0) return <span className="ps-hl">{s}</span>;
  const parts = [s.slice(0, i), <mark key="m" className="mt">{s.slice(i, i + needle.length)}</mark>, s.slice(i + needle.length)].filter((x) => x !== "");
  return <span className="ps-hl">{parts}</span>;
}

const CSS = `
.ps-root{--ink:#141412;--sub:#6C6C69;--faint:#6E6E6B;--mute:#9B9B97;--hair:#E7E6E3;--hair2:#EFEEEB;--soft:#F6F5F3;--indigo:#5856D6;--amber:#FF9500;position:relative;}
.ps-search{position:relative;z-index:30;margin-top:16px;}
.ps-field{height:48px;background:#fff;border:1px solid rgba(0,0,0,.1);border-radius:12px;display:flex;align-items:center;gap:10px;padding:0 12px 0 16px;transition:border-color .15s,box-shadow .2s;}
.ps-field.on{position:relative;z-index:32;border-color:rgba(20,20,18,.12);border-bottom-color:transparent;border-radius:12px 12px 0 0;box-shadow:0 -10px 26px -18px rgba(20,20,18,.14),-16px 0 34px -26px rgba(20,20,18,.12),16px 0 34px -26px rgba(20,20,18,.12);}
.ps-field input{flex:1;background:none;border:0;outline:none;font-size:14px;color:var(--ink);}
.ps-field input::placeholder{color:var(--mute);}
.ps-clr{width:24px;height:24px;flex:none;display:flex;align-items:center;justify-content:center;border:0;background:none;border-radius:6px;color:var(--mute);cursor:pointer;opacity:0;pointer-events:none;transition:opacity .15s,background .15s;}
.ps-field.on .ps-clr,.ps-field:hover .ps-clr{opacity:1;pointer-events:auto;}
.ps-clr:hover{background:var(--soft);color:var(--ink);}
.ps-esc{flex:none;font-size:9px;font-weight:700;letter-spacing:.04em;text-transform:uppercase;color:var(--mute);border:1px solid var(--hair);border-radius:5px;padding:2px 6px;opacity:0;transition:opacity .15s;}
.ps-field.on .ps-esc{opacity:1;}

.ps-dropdown{position:absolute;left:0;right:0;top:100%;z-index:31;background:#fff;border:1px solid rgba(20,20,18,.12);border-top:1px solid var(--hair2);border-radius:0 0 12px 12px;box-shadow:0 28px 64px -24px rgba(20,20,18,.34),-16px 12px 34px -26px rgba(20,20,18,.12),16px 12px 34px -26px rgba(20,20,18,.12);overflow:hidden;animation:psrise .16s cubic-bezier(.3,.7,.3,1);}
@keyframes psrise{from{opacity:0;}to{opacity:1;}}
.ps-phd{display:flex;align-items:center;gap:9px;padding:10px 14px;border-bottom:1px solid var(--hair2);background:var(--soft);}
.ps-phd .lab{font-size:9px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:var(--sub);}
.ps-phd .cnt{font-size:11px;color:var(--mute);}
.ps-chip{margin-left:auto;display:inline-flex;align-items:center;gap:6px;font-size:10.5px;font-weight:650;color:var(--sub);background:#fff;border:1px solid var(--hair);border-radius:999px;padding:3px 9px;cursor:pointer;transition:background .15s,border-color .15s,color .15s;}
.ps-chip.on{background:#ecebfb;border-color:#d7d5f5;color:var(--indigo);}
.ps-chip .bx{width:11px;height:11px;border-radius:3px;border:1.5px solid currentColor;display:inline-flex;align-items:center;justify-content:center;}
.ps-chip.on .bx{background:var(--indigo);border-color:var(--indigo);}
.ps-chip.on .bx::after{content:"";width:5px;height:5px;background:#fff;border-radius:1px;}

.ps-rows{max-height:min(52vh,440px);overflow:auto;}
.ps-row{position:relative;display:flex;align-items:center;gap:13px;padding:11px 16px;cursor:pointer;transition:background .12s;}
.ps-row+.ps-row{border-top:1px solid var(--hair2);}
.ps-row.sel,.ps-row:hover{background:var(--soft);}
/* selection is a leading-edge rule — it never shifts the row under the pointer */
.ps-row.sel::before{content:"";position:absolute;left:0;top:0;bottom:0;width:3px;background:var(--indigo);}
.ps-avc{flex:none;width:32px;height:32px;border-radius:50%;background:#EDECE9;display:flex;align-items:center;justify-content:center;font-size:10px;font-weight:800;color:var(--sub);letter-spacing:.02em;}
.ps-idc{flex:1;min-width:0;}
.ps-nm{display:flex;align-items:baseline;gap:8px;min-width:0;}
.ps-nm .n{font-size:13.5px;font-weight:700;letter-spacing:-.01em;color:var(--ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.ps-nm .d{font-size:10.5px;color:var(--mute);white-space:nowrap;flex:none;}
.ps-ln2{margin-top:2px;font-size:10.5px;color:var(--faint);line-height:1.4;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}
.ps-ln2 .mrn{font-variant-numeric:tabular-nums;color:var(--mute);}
.mt{background:rgba(88,86,214,.16);color:inherit;font-weight:800;border-radius:2px;padding:0 1px;}
/* THE FACTS NEVER MOVE — fixed width, always visible, never replaced by the buttons */
.ps-meta{flex:none;width:184px;text-align:right;font-size:10px;color:var(--faint);line-height:1.5;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.ps-meta .k{font-size:8px;font-weight:800;letter-spacing:.05em;text-transform:uppercase;color:var(--mute);margin-right:4px;}
.ps-meta .none{color:var(--mute);}
.ps-meta .unk{color:var(--mute);font-style:italic;}
/* the action column is reserved permanently, so the row never reflows — the buttons
   simply fade in BESIDE the facts rather than replacing them */
.ps-acts{flex:none;display:flex;align-items:center;justify-content:flex-end;gap:6px;opacity:0;pointer-events:none;transition:opacity .12s;}
.ps-row.sel .ps-acts,.ps-row:hover .ps-acts,.ps-acts:focus-within{opacity:1;pointer-events:auto;}
.ps-b{display:inline-flex;align-items:center;gap:6px;height:30px;padding:0 12px;border-radius:999px;font-size:11px;font-weight:650;cursor:pointer;border:1px solid var(--hair);background:#fff;color:var(--sub);white-space:nowrap;position:relative;transition:border-color .12s,color .12s;}
.ps-b:hover{border-color:var(--ink);color:var(--ink);}
.ps-b.pri{background:var(--ink);border-color:var(--ink);color:#fff;padding:0 14px;}
.ps-b.pri:hover{background:#26282C;border-color:#26282C;color:#fff;}
/* an amber dot means "a human should look" — here: no appointment is booked */
.ps-b .dot{position:absolute;top:-2px;right:-2px;width:8px;height:8px;border-radius:50%;background:var(--amber);border:1.5px solid #fff;}
.ps-row:hover .ps-b .dot,.ps-row.sel .ps-b .dot{border-color:var(--soft);}
@media(max-width:860px){.ps-meta{display:none;}.ps-b .lbl{display:none;}.ps-b{padding:0 9px;}.ps-b.pri{padding:0 10px;}}
.ps-pfoot{display:flex;align-items:center;gap:14px;padding:9px 14px;border-top:1px solid var(--hair2);background:var(--soft);}
.ps-kb{display:inline-flex;align-items:center;gap:5px;font-size:10px;color:var(--mute);}
.ps-kb kbd{font-family:inherit;font-size:9px;font-weight:700;color:var(--sub);background:#fff;border:1px solid var(--hair);border-radius:4px;padding:1px 5px;min-width:16px;text-align:center;}
.ps-seeall{margin-left:auto;display:inline-flex;align-items:center;gap:6px;font-size:11.5px;font-weight:700;color:var(--indigo);background:none;border:0;cursor:pointer;}
.ps-pmsg{padding:26px 16px;text-align:center;color:var(--mute);font-size:12.5px;}

.ps-behind{position:relative;z-index:1;}
.ps-recede{opacity:.42;filter:saturate(.72);pointer-events:none;transition:opacity .2s,filter .2s;}
.ps-scrim{position:absolute;inset:-8px;z-index:5;background:rgba(20,20,18,.03);}

/* committed full-results view */
.ps-rhd{display:flex;align-items:baseline;gap:11px;padding-top:22px;}
.ps-rhd h2{margin:0;font-size:19px;font-weight:800;letter-spacing:-.015em;color:var(--ink);}
.ps-rhd .sub{font-size:11px;color:var(--mute);}
.ps-back{margin-left:auto;display:inline-flex;align-items:center;gap:6px;font-size:12px;font-weight:650;color:var(--sub);background:none;border:0;cursor:pointer;}
.ps-back:hover{color:var(--ink);}
.ps-fbar{display:flex;align-items:center;gap:8px;padding:12px 0;}
.ps-sort{margin-left:auto;display:inline-flex;align-items:center;gap:7px;font-size:11px;color:var(--mute);}
.ps-sortbtn{font-size:11px;font-weight:650;color:var(--ink);background:#fff;border:1px solid var(--hair);border-radius:999px;padding:4px 11px;cursor:pointer;}
.ps-list{border:1px solid var(--hair);border-radius:12px;overflow:hidden;background:#fff;}
.ps-list .ps-row+.ps-row{border-top:1px solid var(--hair2);}
.ps-list .ps-meta{opacity:1;}
.ps-lfoot{display:flex;align-items:center;padding:14px 2px 30px;}
.ps-lfoot .c{font-size:11px;color:var(--mute);}
.ps-more{margin-left:auto;font-size:12px;font-weight:650;color:var(--ink);background:#fff;border:1px solid var(--hair);border-radius:8px;padding:7px 14px;cursor:pointer;}
.ps-more:hover{background:var(--soft);}

/* Edit 3 — actions replace the metadata on the ONE active row, hard right, centred */
.ps-idc{flex:1 1 auto;}
.ps-acts{margin-left:auto;}
.ps-b{justify-content:center;line-height:1;flex:none;}
.ps-row:hover .ps-meta,.ps-row.sel .ps-meta{display:none;}
.ps-row:hover .ps-acts,.ps-row.sel .ps-acts{opacity:1;pointer-events:auto;}
.ps-rows:hover .ps-row.sel:not(:hover) .ps-acts{opacity:0;pointer-events:none;}
.ps-rows:hover .ps-row.sel:not(:hover) .ps-meta{display:block;}
/* stub (registration-only) row — labelled, never hidden, no colour */
.ps-meta.ps-stub{width:auto;color:var(--mute);font-style:italic;}
.ps-regcap{display:inline-flex;align-items:center;height:30px;line-height:1;font-size:10.5px;font-style:italic;color:var(--mute);margin-right:2px;white-space:nowrap;}
/* recents (empty-focus) panel — dashed not-yet treatment until RECENTS_LIVE */
.ps-dropdown.ps-pending{border:1px dashed rgba(20,20,18,.30);border-radius:12px;}
.ps-pendtag{display:inline-flex;align-items:center;margin-left:8px;font-size:8px;font-weight:800;letter-spacing:.06em;text-transform:uppercase;color:var(--mute);border:1px dashed var(--mute);border-radius:5px;padding:2px 6px;}
.ps-recmeta{flex:none;margin-left:auto;text-align:right;font-size:10.5px;color:var(--mute);white-space:nowrap;}
.ps-samplenote{padding:8px 14px;font-size:10.5px;color:var(--mute);border-top:1px dashed rgba(20,20,18,.16);background:var(--soft);}
`;

function Row({ p, q, sel, onEnter, onOpen, onDictate, onFinance, onSchedule }) {
  const name = nameOf(p);
  const age = ageOf(p);
  const sex = fmtSex(p.gender);
  const payer = payerOf(p);
  const last = fmtDate(p.last_office_visit || p.last_visit);
  const next = p.next_appointment ? fmtDate(p.next_appointment) : null;
  const pcp = p.care_provider || null;
  const id = uuidOf(p) || p.mrn || name;
  const stub = !hasMedicalData(p);
  return (
    <div
      className={`ps-row ${sel ? "sel" : ""}`}
      onMouseEnter={onEnter}
      onClick={(e) => { if (e.target.closest("button")) return; onOpen(p); }}
      data-testid={`ps-row-${id}`}
    >
      <span className="ps-avc">{initialsOf(name)}</span>
      <span className="ps-idc">
        <span className="ps-nm">
          <span className="n">{highlight(name, q)}</span>
          <span className="d">{[age != null ? age : null, sex].filter(Boolean).join(" ")}</span>
        </span>
        <span className="ps-ln2">
          {fmtDob(p.dob)}{payer ? <> · {highlight(payer, q)}</> : null} · <span className="mrn">MRN {highlight(String(p.mrn || "—"), q)}</span>
        </span>
      </span>
      {/* Registration-only records stay in the list — labelled, never hidden. */}
      {stub ? (
        <span className="ps-meta ps-stub" data-testid={`ps-stub-${id}`}>registration only · no chart yet</span>
      ) : (
        <span className="ps-meta">
          <span className="k">Last</span>{last || <span className="none">none</span>} · <span className="k">Next</span>{next || <span className="unk" title="revQ has no next-appointment value for this patient — not a statement that none is booked">not known</span>}
          <br /><span className="k">PCP</span>{pcp || <span className="none">—</span>}
        </span>
      )}
      <span className="ps-acts" onMouseDown={(e) => e.preventDefault()}>
        {stub ? (
          <>
            <span className="ps-regcap">registration only</span>
            <button className="ps-b" onClick={() => onOpen(p)} data-testid={`ps-open-${id}`} title="Open the chart on Focus">
              <span className="lbl">Open chart</span> <ArrowRight size={13} />
            </button>
          </>
        ) : (
          <>
            <button className="ps-b" onClick={() => onDictate(p)} data-testid={`ps-dictate-${id}`} title="Open with the recorder running">
              <Mic size={13} /> <span className="lbl">Dictate</span>
            </button>
            <button className="ps-b" onClick={() => onFinance(p)} data-testid={`ps-finance-${id}`} title="Open on the Finance tab">
              <DollarSign size={13} /> <span className="lbl">Finance</span>
            </button>
            <button className="ps-b" onClick={() => onSchedule(p)} data-testid={`ps-schedule-${id}`} title={next ? "Open on the Engagements, Visits & Medical documents tab" : "Open on the Engagements, Visits & Medical documents tab — nothing booked"}>
              <CalendarPlus size={13} /> <span className="lbl">Schedule</span>
              {!next && <span className="dot" aria-hidden="true" />}
            </button>
            <button className="ps-b pri" onClick={() => onOpen(p)} data-testid={`ps-open-${id}`} title="Open the chart on Focus">
              <span className="lbl">Open chart</span> <ArrowRight size={13} />
            </button>
          </>
        )}
      </span>
    </div>
  );
}

export default function PatientSearch({ clientId, onOpenChart, onDictate, onFinance, onSchedule, children }) {
  const [q, setQ] = useState("");
  const [focused, setFocused] = useState(false);
  const [committed, setCommitted] = useState(false);
  const [results, setResults] = useState([]);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [sel, setSel] = useState(-1);
  const [seenYear, setSeenYear] = useState(false);
  const [sortBy, setSortBy] = useState("last");
  const [visible, setVisible] = useState(PAGE);
  const [recentRows, setRecentRows] = useState([]);
  const [recentStatus, setRecentStatus] = useState("idle");
  const inputRef = useRef(null);
  const reqIdRef = useRef(0);
  const recentReqRef = useRef(0);

  useEffect(() => { setQ(""); setResults([]); setStatus("idle"); setCommitted(false); setRecentRows([]); setRecentStatus("idle"); }, [clientId]);

  const loadRecents = useCallback(async () => {
    if (!clientId) return;
    const myId = ++recentReqRef.current;
    setRecentStatus("loading");
    try {
      const { data } = await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: clientId } });
      if (myId !== recentReqRef.current) return;
      const list = (Array.isArray(data?.results) ? data.results : []).filter((p) => p.has_medical_data === true).slice(0, RECENTS_MAX);
      setRecentRows(list); setRecentStatus("done");
    } catch {
      if (myId !== recentReqRef.current) return;
      setRecentRows([]); setRecentStatus("done"); // never error the UI
    }
  }, [clientId]);

  const runSearch = useCallback(async (query) => {
    if (!clientId || !query.trim()) { setResults([]); setStatus("idle"); return; }
    const myId = ++reqIdRef.current;
    setStatus("loading"); setError("");
    try {
      const { data } = await api.get("/api/edi/note-to-claim/pg-patient-search", { params: { client_id: clientId, q: query.trim() } });
      if (myId !== reqIdRef.current) return;
      setResults(Array.isArray(data?.results) ? data.results : []);
      setStatus("done");
    } catch (e) {
      if (myId !== reqIdRef.current) return;
      setError(describeError(e) || "Something went wrong while searching."); setResults([]); setStatus("error");
    }
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    const t = setTimeout(() => runSearch(q), 320);
    return () => clearTimeout(t);
  }, [q, clientId, runSearch]);

  const yearAgo = useMemo(() => Date.now() - 365 * 864e5, []);
  const filtered = useMemo(() => {
    let r = results;
    if (seenYear) r = r.filter((p) => { const d = new Date(p.last_office_visit || p.last_visit || 0); return !isNaN(d) && d.getTime() >= yearAgo; });
    const sorted = [...r];
    if (sortBy === "name") sorted.sort((a, b) => nameOf(a).localeCompare(nameOf(b)));
    else sorted.sort((a, b) => new Date(b.last_office_visit || b.last_visit || 0) - new Date(a.last_office_visit || a.last_visit || 0));
    return sorted;
  }, [results, seenYear, sortBy, yearAgo]);
  const regCount = useMemo(() => filtered.filter((p) => !hasMedicalData(p)).length, [filtered]);
  const recents = RECENTS_LIVE ? recentRows : RECENTS_FIXTURE;

  const total = filtered.length;
  const dropRows = filtered.slice(0, DROPDOWN_MAX);
  const searching = focused && !!q.trim() && !committed;
  const showRecents = focused && !q.trim() && !committed;

  useEffect(() => { if (RECENTS_LIVE && showRecents && clientId && recentStatus === "idle") loadRecents(); }, [showRecents, clientId, recentStatus, loadRecents]);

  const blur = () => { inputRef.current?.blur(); setFocused(false); };
  const commit = () => { if (!total) return; setCommitted(true); setFocused(false); setSel(-1); setVisible(PAGE); inputRef.current?.blur(); document.querySelector("[data-testid=patients-view]")?.scrollIntoView({ behavior: "smooth", block: "start" }); };
  const backToday = () => { setCommitted(false); setQ(""); setResults([]); setStatus("idle"); setSel(-1); };
  const openChart = (p) => { if (typeof onOpenChart === "function") onOpenChart(p); };

  const onChange = (e) => { setQ(e.target.value); setSel(-1); if (committed) { setCommitted(false); setFocused(true); } };
  const onKeyDown = (e) => {
    const list = showRecents ? recents : dropRows;
    if (e.key === "Escape") { e.preventDefault(); if (committed) backToday(); else blur(); return; }
    if (e.key === "Enter") {
      if ((e.metaKey || e.ctrlKey) && !showRecents) { e.preventDefault(); commit(); return; }
      const pick = sel >= 0 ? list[sel] : list[0];
      if (pick) { e.preventDefault(); openChart(pick); }
      return;
    }
    if (!(showRecents || searching) || !list.length) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setSel((s) => Math.min((s < 0 ? -1 : s) + 1, list.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setSel((s) => Math.max((s <= 0 ? 1 : s) - 1, 0)); }
  };

  const Chip = ({ on, onClick, label, testid }) => (
    <button className={`ps-chip ${on ? "on" : ""}`} onMouseDown={(e) => e.preventDefault()} onClick={onClick} data-testid={testid}>
      <span className="bx" />{label}
    </button>
  );

  return (
    <div className="ps-root" data-testid="patient-search">
      <style>{CSS}</style>

      <div className="ps-search">
        <div className={`ps-field ${searching ? "on" : ""}`}>
          <Search size={16} className="text-[var(--mute)]" style={{ flex: "none", color: "#9B9B97" }} />
          <input
            ref={inputRef}
            value={q}
            onChange={onChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            onKeyDown={onKeyDown}
            placeholder="Search by patient name, MRN or payer…"
            data-testid="patients-search-input"
            autoComplete="off"
          />
          {status === "loading" && searching && <Loader2 size={15} className="animate-spin" style={{ color: "#9B9B97", flex: "none" }} />}
          <button className="ps-clr" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(""); setCommitted(false); inputRef.current?.focus(); }} data-testid="patients-search-clear" aria-label="Clear"><X size={15} /></button>
          <span className="ps-esc">esc</span>
        </div>

        {searching && (
          <div className="ps-dropdown" onMouseDown={(e) => e.preventDefault()} data-testid="patients-search-dropdown">
            <div className="ps-phd">
              <span className="lab">Patients</span>
              <span className="cnt">{status === "loading" ? "searching…" : total ? `Top ${Math.min(DROPDOWN_MAX, total)} of ${total} matching “${q.trim()}”${regCount ? ` · ${regCount} registration only` : ""}` : `no matches for “${q.trim()}”`}</span>
            </div>
            {status === "error" ? (
              <div className="ps-pmsg"><AlertTriangle size={18} style={{ margin: "0 auto 6px", color: "#B42318" }} /><div>{error}</div></div>
            ) : total === 0 && status === "done" ? (
              <div className="ps-pmsg"><SearchX size={18} style={{ margin: "0 auto 6px" }} />No patients found</div>
            ) : (
              <div className="ps-rows">
                {dropRows.map((p, i) => (
                  <Row key={uuidOf(p) || p.mrn || i} p={p} q={q} sel={sel === i} onEnter={() => setSel(i)}
                    onOpen={openChart} onDictate={onDictate} onFinance={onFinance} onSchedule={onSchedule} />
                ))}
              </div>
            )}
            <div className="ps-pfoot">
              <span className="ps-kb"><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
              <span className="ps-kb"><kbd>↵</kbd> open chart</span>
              <span className="ps-kb"><kbd>esc</kbd> dismiss</span>
              {total > 0 && <button className="ps-seeall" onClick={commit} data-testid="patients-see-all">See all {total} matches <ArrowRight size={13} /></button>}
            </div>
          </div>
        )}

        {showRecents && (
          <div className="ps-dropdown" onMouseDown={(e) => e.preventDefault()} data-testid="patients-recents-dropdown">
            <div className="ps-phd">
              <span className="lab">Recent charts</span>
              <span className="cnt">{recentStatus === "loading" ? "loading…" : recents.length ? "newest first" : ""}</span>
            </div>
            {recentStatus === "loading" ? (
              <div className="ps-pmsg" data-testid="patients-recents-loading"><Loader2 size={16} className="animate-spin" style={{ margin: "0 auto 6px", color: "#9B9B97" }} />Loading recent charts…</div>
            ) : recents.length ? (
              <div className="ps-rows">
                {recents.map((p, i) => {
                  const name = nameOf(p), age = ageOf(p), last = fmtDate(p.last_office_visit || p.last_visit);
                  return (
                    <div key={uuidOf(p) || p.mrn || i} className={`ps-row ${sel === i ? "sel" : ""}`} onMouseEnter={() => setSel(i)} onClick={() => openChart(p)} data-testid={`ps-recent-${uuidOf(p) || p.mrn || i}`}>
                      <span className="ps-avc">{initialsOf(name)}</span>
                      <span className="ps-idc">
                        <span className="ps-nm"><span className="n">{name}</span><span className="d">{[age != null ? age : null, fmtSex(p.gender)].filter(Boolean).join(" ")}</span></span>
                        <span className="ps-ln2"><span className="mrn">{p.mrn ? `MRN ${p.mrn}` : "no MRN on file"}</span></span>
                      </span>
                      {last ? <span className="ps-recmeta">Last seen {last}</span> : null}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="ps-pmsg" data-testid="patients-recents-empty">
                <Search size={18} style={{ margin: "0 auto 6px", color: "#9B9B97" }} />
                Start typing a name, MRN or payer to find a patient.
                <div style={{ marginTop: 4, fontSize: 11, color: "var(--mute)" }}>No recently active charts to show yet.</div>
              </div>
            )}
            <div className="ps-pfoot">
              <span className="ps-kb"><kbd>↑</kbd><kbd>↓</kbd> navigate</span>
              <span className="ps-kb"><kbd>↵</kbd> open chart</span>
              <span className="ps-kb"><kbd>esc</kbd> dismiss</span>
            </div>
          </div>
        )}
      </div>

      {/* Behind the search field: committed results OR the idle landing (dimmed while searching) */}
      <div className="ps-behind">
        {committed ? (
          <div data-testid="patients-committed">
            <div className="ps-rhd">
              <h2>{total} {total === 1 ? "patient" : "patients"} matching “{q.trim()}”</h2>
              <span className="sub">name, MRN and payer searched{regCount ? ` · ${regCount} registration only` : ""}</span>
              <button className="ps-back" onClick={backToday} data-testid="patients-back-today"><ArrowLeft size={14} /> Back to today</button>
            </div>
            <div className="ps-fbar">
              <Chip on={seenYear} onClick={() => setSeenYear((v) => !v)} label="Seen in the last year" testid="ps-chip-seenyear" />
              <span className="ps-sort">Sort
                <button className="ps-sortbtn" onClick={() => setSortBy((s) => (s === "last" ? "name" : "last"))} data-testid="patients-sort">
                  {sortBy === "last" ? "Last seen" : "Name (A–Z)"}
                </button>
              </span>
            </div>
            {total === 0 ? (
              <div className="ps-list"><div className="ps-pmsg"><SearchX size={18} style={{ margin: "0 auto 6px" }} />No patients match these filters</div></div>
            ) : (
              <div className="ps-list">
                {filtered.slice(0, visible).map((p, i) => (
                  <Row key={uuidOf(p) || p.mrn || i} p={p} q={q} sel={false} onEnter={() => {}}
                    onOpen={openChart} onDictate={onDictate} onFinance={onFinance} onSchedule={onSchedule} />
                ))}
              </div>
            )}
            <div className="ps-lfoot">
              <span className="c">Showing {Math.min(visible, total)} of {total}</span>
              {visible < total && <button className="ps-more" onClick={() => setVisible((v) => v + PAGE)} data-testid="patients-load-more">Load {Math.min(PAGE, total - visible)} more</button>}
            </div>
          </div>
        ) : (
          <div style={{ position: "relative" }}>
            <div className={searching ? "ps-recede" : ""}>{children}</div>
            {searching && <div className="ps-scrim" onClick={blur} data-testid="patients-search-scrim" />}
          </div>
        )}
      </div>
    </div>
  );
}
